import { Container, Divider } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { Grid } from "@mui/material";
import axios from "axios";
import {
  AutoCompleteComponent,
  CurrencyDisplay,
  ModalError,
  Page,
  PrimaryButton,
  SearchTextInput,
  SelectInput,
  SkeletonComponent,
  StatusChip,
  TableAction,
} from "components";
import { ButtonGroupTop } from "components/atoms/Button";
import { InnoTable } from "inno-ui";
import { HeaderTitle } from "layouts";
import { debounce } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { getErrors, textCapitalization } from "utils";
import ModalFormLoan from "./Components/ModalFormLoan";

const LoanPage = ({ userLogin }) => {
  const classes = globalStyles();
  const history = useHistory();
  const location = useLocation();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const urlParams = new URLSearchParams(location.search);
  const paramsSearch = urlParams?.get("search");
  const paramsPage = urlParams?.get("page");
  const paramsSize = urlParams?.get("per_page");
  const paramsStatus = urlParams?.get("status");
  const paramsComposer = urlParams?.get("composer_id");

  const [loadingPage, setLoadingPage] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: Number(paramsPage) || 1,
    per_page: Number(paramsSize) || 10,
    search: paramsSearch || "",
    status: paramsStatus || "",
    composer_id: Number(paramsComposer) || "",
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [optionComposer, setOptionComposer] = useState([]);
  const [modalPreload, setModalPreload] = useState(null);
  const [modalFor, setModalFor] = useState(null);

  const handleChangePageParams = (value, key) => {
    urlParams.set(key, value);
    history.replace({
      pathname: location.pathname,
      search: urlParams.toString(),
    });
  };

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
    handleChangePageParams(value, key);
    if (key === "search") {
      handleChangePageParams(value, "page");
    }
  };

  const getOptionComposer = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(
        `${hardBaseUrl}/publisher/transaction-composer`,
        { headers }
      );

      const modifiedData = (res?.data?.data || [])
        ?.filter(({ composer_name }) => composer_name)
        ?.map(item => ({
          label: item.composer_name,
          id: item.composer_id,
        }));
      const uniqueItems = modifiedData.filter(
        (item, index, array) =>
          index === array.findIndex(el => el.label === item.label)
      );
      setOptionComposer(uniqueItems);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const getDataTable = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(`${hardBaseUrl}/publisher/transaction-loan`, {
        headers,
        params: queryParams,
      });

      const { data, query } = res?.data?.data;
      setDataTable(data);
      setTableTotalPage(query?.total_page);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const debounceDataTable = useCallback(
    debounce(() => {
      getDataTable();
    }, 500),
    [queryParams]
  );

  useEffect(() => {
    getOptionComposer();
  }, []);
  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      getDataTable();
    }
  }, [queryParams, debounceDataTable]);

  return (
    <Page className={classes.root} title="Loan">
      <Container maxWidth={false}>
        <HeaderTitle title="Loan Transaction" breadcrumbData={breadcrumbData} />
        <ButtonGroupTop />
        <Divider className={classes?.flatDivider} />
        <Grid container justifyContent="space-between" my="16px">
          <Grid item>
            <SearchTextInput
              placeholder="Search"
              value={queryParams?.search}
              onChange={e =>
                handleChangeQueryParams(e?.target?.value, "search")
              }
            />
          </Grid>
          <Grid item>
            <Grid container columnSpacing={1} alignItems="center">
              <Grid item>
                <AutoCompleteComponent
                  options={optionComposer}
                  label="Composer/Author"
                  value={
                    optionComposer.find(
                      option => option.id === Number(queryParams?.composer_id)
                    ) || null
                  }
                  onChange={id => handleChangeQueryParams(id, "composer_id")}
                  size="small"
                  width={250}
                />
              </Grid>
              <Grid item>
                <SelectInput
                  label="Status"
                  options={listStatus}
                  optionKey="value"
                  optionLabel="name"
                  value={queryParams?.status || ""}
                  onChange={e =>
                    handleChangeQueryParams(e?.target?.value, "status")
                  }
                  placeholder="All"
                  width={200}
                />
              </Grid>
              <Grid item>
                <PrimaryButton
                  label="Add Loan"
                  startIcon={<Add />}
                  onClick={() => {
                    setModalPreload(null);
                    setModalFor("loan");
                  }}
                  size="large"
                />
              </Grid>
              <Grid item>
                <PrimaryButton
                  label="Payment"
                  startIcon={<Add />}
                  onClick={() => {
                    setModalPreload(null);
                    setModalFor("payment");
                  }}
                  size="large"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {loadingPage ? (
          <SkeletonComponent variant="wave" />
        ) : (
          <InnoTable
            isLoading={false}
            columns={columnTable}
            items={dataTable}
            page={queryParams?.page}
            rowsPerPage={queryParams?.per_page}
            totalPage={tableTotalPage}
            handleChangePage={(_, page) =>
              handleChangeQueryParams(page, "page")
            }
            handleChangeRowsPerPage={e =>
              handleChangeQueryParams(e?.target?.value, "per_page")
            }
            isHaveAction={true}
            renderAction={item => {
              const isWaitingApproval = item.status === "waiting_for_approval";
              return (
                <TableAction
                  handleView={() =>
                    history.push({
                      pathname: `/admin/loan/${item.publisher_transaction_id}`,
                      state: { customer: item },
                    })
                  }
                  handleEdit={() => {
                    setModalPreload(item?.publisher_transaction_id);
                    setModalFor("loan");
                  }}
                  disabledEdit={!isWaitingApproval}
                />
              );
            }}
          />
        )}
      </Container>
      {modalFor && (
        <ModalFormLoan
          modalFor={modalFor}
          open={Boolean(modalFor)}
          onClose={() => setModalFor(null)}
          optionComposer={optionComposer}
          preload={modalPreload}
          getDataTable={getDataTable}
          userLogin={userLogin}
        />
      )}
    </Page>
  );
};

const listStatus = [
  {
    name: "Waiting for Approval",
    value: "waiting_for_approval",
  },
  {
    name: "Approved",
    value: "approved",
  },
  {
    name: "Rejected",
    value: "rejected",
  },
  {
    name: "Active",
    value: "active",
  },
  {
    name: "Paid",
    value: "paid",
  },
];
const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Transaction",
    link: "/admin/loan",
  },
  {
    label: "Loan",
    active: true,
  },
];
const typeChip = {
  active: "active",
  approved: "success",
  paid: "success",
  waiting_for_approval: "warning",
  rejected: "danger",
};
const columnTable = [
  {
    name: "composer",
    title: "Composer/Author Name",
  },
  {
    name: "date",
    title: "Date",
    renderText: item => moment(item).format("DD MMM YYYY"),
  },
  {
    name: "tenor",
    title: "Tenor",
  },
  {
    name: "loan_value",
    title: "Loan Value",
    headerAlign: "right",
    renderText: item => (
      <CurrencyDisplay value={item} decimalScale={2} prefix="Rp" />
    ),
  },
  {
    name: "status",
    title: "Status",
    renderText: item => (
      <StatusChip
        label={
          item === "waiting_for_approval"
            ? "Waiting for Approval"
            : textCapitalization(item)
        }
        type={typeChip[item]}
      />
    ),
  },
];
export default LoanPage;
